import classNames from "classnames";
import { Form, Formik, useFormikContext } from "formik";
import { FC, useCallback, useMemo, useRef } from "react";

import AsyncSearchableSelect from "components/AsyncSearchableSelect";
import Button from "components/Button";
import DatePicker from "components/DatePicker";
import Select from "components/Select";
import TextInput from "components/TextInput";

import useValidation from "hooks/useValidation";

import { AssetsApi, CompaniesApi, PortfoliosApi } from "services/http";
import { QUERY_PARAMS } from "utils/constants";
import { QueryHelper } from "utils/helpers";

import { BaseOption, UserRole } from "entities/common";
import {
  EXPORT_FORMAT_DEFAULT,
  EXPORT_FORMAT_OPTIONS,
  ExportFormat,
  REPORT_ERRORS,
  REPORT_LEVEL_DEFAULT,
  REPORT_LEVEL_OPTIONS,
  REPORT_NAME_MAX_LENGTH,
  REPORT_PERIOD_CUSTOM,
  REPORT_PERIOD_DEFAULT,
  REPORT_PERIOD_OPTIONS,
  ReportLevel,
  ReportPeriod,
  buildSummaryLines,
  getMaxSelectableDate,
  parseDateValue,
} from "entities/reports";

import { RootStore } from "store";
import { useAppSelector } from "store/hooks";

import { buildValidationSchema } from "../validationSchema";

import styles from "../styles.module.scss";

export type ReportFormValues = {
  name: string;
  level: ReportLevel;
  companyId: string;
  companyLabel: string;
  portfolioId: string;
  portfolioLabel: string;
  assetId: string;
  assetLabel: string;
  period: ReportPeriod;
  startDate: string;
  endDate: string;
  format: ExportFormat;
};

type PropTypes = {
  onClose?: () => void;
  onSubmit?: (values: ReportFormValues) => void;
  initialLevel?: ReportLevel;
  initialCompany?: BaseOption;
  initialPortfolio?: BaseOption;
  initialAsset?: BaseOption;
};

const SELECT_PAGE_SIZE = 50;

const FormFields: FC<{ onCancel: () => void; isAdmin: boolean }> = ({ onCancel, isAdmin }) => {
  const formRef = useRef<HTMLFormElement>(null);

  const { values, errors, touched, setFieldValue, setFieldTouched, setValues, handleChange, handleSubmit } =
    useFormikContext<ReportFormValues>();

  useValidation(formRef, errors);

  const isAssetLevel = values.level === "asset";
  const isCustomRange = values.period === REPORT_PERIOD_CUSTOM;

  const searchCompanies = useCallback(async (search: string, page: number) => {
    try {
      const { data } = await CompaniesApi.getList(
        QueryHelper.getQueryString({
          [QUERY_PARAMS.SEARCH]: search || undefined,
          [QUERY_PARAMS.PAGE]: page,
          [QUERY_PARAMS.PAGE_SIZE]: SELECT_PAGE_SIZE,
        }),
      );
      const options: BaseOption[] = (data?.items ?? []).map((company) => ({
        value: String(company.id),
        label: company.name,
      }));
      return { options, hasMore: (data?.total ?? 0) > page * SELECT_PAGE_SIZE };
    } catch {
      return { options: [], hasMore: false };
    }
  }, []);

  const searchPortfolios = useCallback(
    async (search: string, page: number) => {
      try {
        const { data } = await PortfoliosApi.getList(
          QueryHelper.getQueryString({
            [QUERY_PARAMS.SEARCH]: search || undefined,
            [QUERY_PARAMS.PAGE]: page,
            [QUERY_PARAMS.PAGE_SIZE]: SELECT_PAGE_SIZE,
            ...(values.companyId ? { [QUERY_PARAMS.COMPANY_ID]: Number(values.companyId) } : {}),
          }),
        );
        const options: BaseOption[] = (data?.items ?? []).map((portfolio) => ({
          value: String(portfolio.id),
          label: portfolio.name,
          companyId: portfolio.company_id,
          companyName: portfolio.company_name,
        }));
        return { options, hasMore: (data?.total ?? 0) > page * SELECT_PAGE_SIZE };
      } catch {
        return { options: [], hasMore: false };
      }
    },
    [values.companyId],
  );

  const searchAssets = useCallback(
    async (search: string, page: number) => {
      try {
        const { data } = await AssetsApi.getList(
          QueryHelper.getQueryString({
            [QUERY_PARAMS.SEARCH]: search || undefined,
            [QUERY_PARAMS.PAGE]: page,
            [QUERY_PARAMS.PAGE_SIZE]: SELECT_PAGE_SIZE,
            ...(values.portfolioId ? { [QUERY_PARAMS.PORTFOLIO_ID]: Number(values.portfolioId) } : {}),
            ...(values.companyId ? { [QUERY_PARAMS.COMPANY_ID]: Number(values.companyId) } : {}),
          }),
        );
        const options: BaseOption[] = (data?.items ?? []).map((asset) => ({
          value: String(asset.id),
          label: asset.name,
          portfolioId: (asset as { portfolio_id?: number }).portfolio_id,
        }));
        return { options, hasMore: (data?.total ?? 0) > page * SELECT_PAGE_SIZE };
      } catch {
        return { options: [], hasMore: false };
      }
    },
    [values.companyId, values.portfolioId],
  );

  // Multi-field updates go through a single setValues call: sequential setFieldValue
  // calls each validate a stale values snapshot, so the last one restores errors
  // already fixed by the fields set before it.
  const handleLevelChange = useCallback(
    (level: ReportLevel) => () => {
      setValues((prev) => ({
        ...prev,
        level,
        ...(level === "portfolio" ? { assetId: "", assetLabel: "" } : {}),
      }));
    },
    [setValues],
  );

  const handleFormatChange = useCallback(
    (value: ExportFormat) => () => setFieldValue("format", value),
    [setFieldValue],
  );

  const handleCompanyChange = useCallback(
    (value: string, label: string) => {
      setValues((prev) => ({
        ...prev,
        companyId: value,
        companyLabel: label,
        ...(prev.portfolioId ? { portfolioId: "", portfolioLabel: "" } : {}),
        ...(prev.assetId ? { assetId: "", assetLabel: "" } : {}),
      }));
    },
    [setValues],
  );

  const handlePortfolioChange = useCallback(
    (value: string, label: string, option?: BaseOption) => {
      setValues((prev) => ({
        ...prev,
        portfolioId: value,
        portfolioLabel: label,
        ...(!prev.companyId && option?.companyId
          ? { companyId: String(option.companyId), companyLabel: (option.companyName as string) ?? "" }
          : {}),
        ...(prev.assetId ? { assetId: "", assetLabel: "" } : {}),
      }));
    },
    [setValues],
  );

  const handleAssetChange = useCallback(
    async (value: string, label: string, option?: BaseOption) => {
      setValues((prev) => ({ ...prev, assetId: value, assetLabel: label }));

      const optionPortfolioId = option?.portfolioId;
      if (!value || values.portfolioId || optionPortfolioId == null) return;
      try {
        const { data: portfolio } = await PortfoliosApi.getCurrent(Number(optionPortfolioId));
        setValues((prev) => ({
          ...prev,
          portfolioId: String(portfolio.id),
          portfolioLabel: portfolio.name,
          ...(!prev.companyId
            ? { companyId: String(portfolio.company_id), companyLabel: portfolio.company_name }
            : {}),
        }));
      } catch {
        return;
      }
    },
    [setValues, values.portfolioId],
  );

  const handlePeriodChange = useCallback(
    (value: string) => {
      setValues((prev) => ({
        ...prev,
        period: value as ReportPeriod,
        ...(value !== REPORT_PERIOD_CUSTOM ? { startDate: "", endDate: "" } : {}),
      }));
      // A submit marks every field touched, the date fields included even though
      // they are only on screen for a custom range. Without this they would
      // appear already flagged the moment the user switches to that range.
      setFieldTouched("startDate", false, false);
      setFieldTouched("endDate", false, false);
    },
    [setValues, setFieldTouched],
  );

  const handleStartDateChange = useCallback((value: string) => setFieldValue("startDate", value), [setFieldValue]);

  const handleEndDateChange = useCallback((value: string) => setFieldValue("endDate", value), [setFieldValue]);

  const summaryLines = useMemo(() => buildSummaryLines(values, isAdmin), [values, isAdmin]);

  const maxSelectableDate = useMemo(() => getMaxSelectableDate(), []);

  const startMaxDate = useMemo(() => {
    const end = parseDateValue(values.endDate);
    return end && end < maxSelectableDate ? end : maxSelectableDate;
  }, [values.endDate, maxSelectableDate]);

  const endMinDate = useMemo(() => parseDateValue(values.startDate), [values.startDate]);

  const endDateError = touched.endDate
    ? (errors.endDate ?? (errors.startDate === REPORT_ERRORS.PERIOD_TOO_LONG || undefined))
    : undefined;

  const portfolioField = (
    <AsyncSearchableSelect
      label="Portfolio"
      placeholder="Select portfolio"
      value={values.portfolioId}
      selectedLabel={values.portfolioLabel}
      onSearch={searchPortfolios}
      onChange={handlePortfolioChange}
      error={touched.portfolioId ? (errors.portfolioId as string | undefined) : undefined}
      isRequired
    />
  );

  return (
    <Form ref={formRef} className={styles.content} onSubmit={handleSubmit}>
      <p className={styles.description}>Configure report parameters and filters</p>

      <TextInput
        id="name"
        name="name"
        label="Report name"
        placeholder="Report name"
        value={values.name}
        onChange={handleChange}
        maxLength={REPORT_NAME_MAX_LENGTH}
      />

      <div className={styles.group}>
        <p className={styles.groupLabel}>Report level</p>

        <div className={styles.segments}>
          {REPORT_LEVEL_OPTIONS.map((item) => (
            <button
              key={item.value}
              type="button"
              className={classNames(styles.segment, { [styles.segmentActive]: item.value === values.level })}
              onClick={handleLevelChange(item.value as ReportLevel)}
            >
              <span className={styles.segmentTitle}>{item.title}</span>
              <span className={styles.segmentSub}>{item.subtitle}</span>
            </button>
          ))}
        </div>
      </div>

      {isAdmin ? (
        <div className={styles.row2}>
          <AsyncSearchableSelect
            label="Company"
            placeholder="Select company"
            value={values.companyId}
            selectedLabel={values.companyLabel}
            onSearch={searchCompanies}
            onChange={handleCompanyChange}
            error={touched.companyId ? (errors.companyId as string | undefined) : undefined}
            isRequired
          />

          {portfolioField}
        </div>
      ) : (
        portfolioField
      )}

      {isAssetLevel && (
        <AsyncSearchableSelect
          label="Asset"
          placeholder="Select asset"
          value={values.assetId}
          selectedLabel={values.assetLabel}
          onSearch={searchAssets}
          onChange={handleAssetChange}
          error={touched.assetId ? (errors.assetId as string | undefined) : undefined}
          isRequired
        />
      )}

      <Select
        options={REPORT_PERIOD_OPTIONS}
        value={values.period}
        onChange={handlePeriodChange}
        label="Report period"
        error={touched.period ? errors.period : undefined}
        isRequired
      />

      {isCustomRange && (
        <div className={styles.row2}>
          <DatePicker
            label="Start date"
            placeholder="DD.MM.YYYY"
            value={values.startDate}
            onChange={handleStartDateChange}
            maxDate={startMaxDate}
            error={touched.startDate ? errors.startDate : undefined}
            isRequired
          />

          <DatePicker
            label="End date"
            placeholder="DD.MM.YYYY"
            value={values.endDate}
            onChange={handleEndDateChange}
            minDate={endMinDate}
            maxDate={maxSelectableDate}
            error={endDateError}
            isRequired
          />
        </div>
      )}

      <div className={styles.group}>
        <p className={styles.groupLabel}>Export format</p>

        <div className={styles.segments}>
          {EXPORT_FORMAT_OPTIONS.map((item) => (
            <button
              key={item.value}
              type="button"
              className={classNames(styles.segment, { [styles.segmentActive]: item.value === values.format })}
              onClick={handleFormatChange(item.value as ExportFormat)}
            >
              <span className={styles.segmentTitle}>{item.title}</span>
              <span className={styles.segmentSub}>{item.subtitle}</span>
            </button>
          ))}
        </div>
      </div>

      <div className={styles.summary}>
        <p className={styles.summaryTitle}>Report configuration summary</p>

        <ul className={styles.summaryList}>
          {summaryLines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </div>

      <div className={styles.note}>
        <p className={styles.noteTitle}>Note on report values:</p>
        <p className={styles.noteText}>
          This report is always generated based on the standard DM-HSD-GU119 default values and limits for each metric.
          Custom limits configured in your limit settings apply only to dashboard indicators and alert notifications,
          and do not affect report results.
        </p>
      </div>

      <div className={styles.buttons}>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>

        <Button type="submit" variant="primary">
          Generate report
        </Button>
      </div>
    </Form>
  );
};

const GenerateReportContent: FC<PropTypes> = ({
  onClose,
  onSubmit,
  initialLevel,
  initialCompany,
  initialPortfolio,
  initialAsset,
}) => {
  const authUser = useAppSelector((state: RootStore) => state.auth.user);
  const isAdmin = authUser?.role === UserRole.ADMINISTRATOR;

  const initialValues = useMemo<ReportFormValues>(
    () => ({
      name: "",
      level: initialLevel ?? REPORT_LEVEL_DEFAULT,
      companyId: initialCompany?.value ?? "",
      companyLabel: initialCompany?.label ?? "",
      portfolioId: initialPortfolio?.value ?? "",
      portfolioLabel: initialPortfolio?.label ?? "",
      assetId: initialAsset?.value ?? "",
      assetLabel: initialAsset?.label ?? "",
      period: REPORT_PERIOD_DEFAULT,
      startDate: "",
      endDate: "",
      format: EXPORT_FORMAT_DEFAULT,
    }),
    [initialLevel, initialCompany, initialPortfolio, initialAsset],
  );

  const validationSchema = useMemo(() => buildValidationSchema(isAdmin), [isAdmin]);

  const handleCancel = useCallback(() => onClose?.(), [onClose]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={(submitted) => onSubmit?.({ ...submitted, name: submitted.name.trim() })}
    >
      <FormFields onCancel={handleCancel} isAdmin={isAdmin} />
    </Formik>
  );
};

export default GenerateReportContent;
